/**
 * TAHLEEL.ai — Agent Sidebar
 * Left panel showing all 10 agents grouped into Core (live) and Coming Soon (disabled).
 */

import React from 'react';
import {
  Target, Users, TrendingUp, BookOpen,
  Activity, Search, BarChart3, UserPlus,
  Calendar, MessageSquare, Plus
} from 'lucide-react';

const AGENTS = [
  // Core Agents — Live
  { id: 'tactical',    name: 'Tactical Analysis',  desc: 'Match breakdown',       icon: Target,         live: true },
  { id: 'formation',   name: 'Formation Detection', desc: 'Detect & counter',      icon: Users,          live: true },
  { id: 'movement',    name: 'Player Movement',     desc: 'Heat maps',             icon: TrendingUp,     live: true },
  { id: 'training',    name: 'Training Plan',       desc: 'Drills & schedules',    icon: BookOpen,       live: true },
  // Coming Soon
  { id: 'injury',      name: 'Movement Risk',         desc: 'Risk flags & fatigue',  icon: Activity,       live: true },
  { id: 'scout',       name: 'Opponent Scout',       desc: 'Deep scouting',         icon: Search,         live: false },
  { id: 'performance', name: 'Performance Analytics', desc: 'Stats & trends',       icon: BarChart3,      live: false },
  { id: 'recruit',     name: 'Recruitment & Talent ID', desc: 'Transfer targets',  icon: UserPlus,       live: false },
  { id: 'planner',     name: 'Session Planner',      desc: 'Weekly scheduling',     icon: Calendar,       live: false },
  { id: 'comms',       name: 'Communication',        desc: 'Player motivation',     icon: MessageSquare,  live: false },
];

export { AGENTS };

const AgentSidebar = ({ activeAgent, onAgentSelect, onNewAnalysis }) => {
  const coreAgents = AGENTS.filter(a => a.live);
  const soonAgents = AGENTS.filter(a => !a.live);

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">

      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-100">
        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Agents</h2>
      </div>

      {/* Scrollable agent list */}
      <div className="flex-1 overflow-y-auto py-2">

        {/* Core Agents */}
        <div className="px-3 mb-1">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Core Agents</p>
        </div>
        {coreAgents.map(agent => {
          const Icon = agent.icon;
          const isActive = activeAgent === agent.id;
          return (
            <button
              key={agent.id}
              onClick={() => onAgentSelect(agent.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${
                isActive
                  ? 'border-r-2'
                  : 'hover:bg-gray-50'
              }`}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-amber-600' : 'text-gray-400'}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold truncate ${isActive ? 'text-amber-700' : 'text-gray-800'}`}>
                  {agent.name}
                </div>
                <div className="text-[10px] text-gray-400 truncate">{agent.desc}</div>
              </div>
              <span className="flex-shrink-0 text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                Live
              </span>
            </button>
          );
        })}

        {/* Divider */}
        <div className="my-3 mx-4 border-t border-gray-100"></div>

        {/* Coming Soon */}
        <div className="px-3 mb-1">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Coming Soon</p>
        </div>
        {soonAgents.map(agent => {
          const Icon = agent.icon;
          return (
            <div
              key={agent.id}
              className="w-full flex items-center gap-3 px-4 py-2.5 opacity-40 cursor-not-allowed"
            >
              <Icon className="h-4 w-4 flex-shrink-0 text-gray-300" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-400 truncate">{agent.name}</div>
                <div className="text-[10px] text-gray-300 truncate">{agent.desc}</div>
              </div>
              <span className="flex-shrink-0 text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                Soon
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom action */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={onNewAnalysis}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all"
          style={{ background: '#f59e0b', color: '#0a1628' }}
        >
          <Plus className="h-3.5 w-3.5" />
          <span>New Analysis</span>
        </button>
      </div>
    </div>
  );
};

export default AgentSidebar;
